import { getConfig } from './config.js';
import { logout, getAuthToken, initAuth } from './auth.js';

/**
 * Lightswitch SDK Core API
 * 
 * Handles authenticated API requests to the Lightswitch backend.
 * Pure JavaScript implementation with no framework dependencies.
 */


/**
 * Wait for auth token to be available with retry logic
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delay - Delay between retries in milliseconds
 * @returns {Promise<string>} Auth token
 */
const waitForAuthToken = async function () {
  let maxRetries = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 10;
  let delay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 100;
  for (let i = 0; i < maxRetries; i++) {
    const token = getAuthToken();
    if (token) {
      return token;
    }

    // If no token and this is the first attempt, try to initialize auth
    if (i === 0) {
      try {
        await initAuth();
        const tokenAfterInit = getAuthToken();
        if (tokenAfterInit) {
          return tokenAfterInit;
        }
      } catch (error) {
        console.warn('Failed to initialize auth during callWithAuth:', error);
      }
    }

    // Wait before next retry
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  throw new Error('No authentication token available after waiting. Please login.');
};

/**
 * Make an authenticated call with custom logic
 * @param {Function} callFunction - Function that performs the actual call
 * @param {Object} [options={}] - Additional options
 * @param {number} [options.maxRetries=10] - Maximum retries to wait for auth token
 * @param {number} [options.retryDelay=100] - Delay between retries in milliseconds
 * @returns {Promise<any>} Result from the call function
 */
const callWithAuth = async function (callFunction) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    maxRetries = 10,
    retryDelay = 100,
    ...otherOptions
  } = options;
  try {
    const authToken = await waitForAuthToken(maxRetries, retryDelay);
    const config = getConfig();
    if (!config.publishableKey) {
      throw new Error('No publishable key configured');
    }
    const authHeaders = {
      'Authorization': `Bearer ${authToken}`,
      'X-Publishable-Key': config.publishableKey,
      'X-App-Slug': config.appSlug || 'default',
      'Content-Type': 'application/json',
      ...otherOptions.headers
    };
    const result = await callFunction(authHeaders, otherOptions);
    return result;
  } catch (error) {
    // Handle token expiration
    if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
      await logout();
      throw new Error('Authentication expired. Please login again.');
    }
    console.error('Authenticated call error:', error);
    throw error;
  }
};

/**
 * Make an authenticated API request
 * @param {string} url - The URL to make the request to
 * @param {Object} [options={}] - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithAuth = async function (url) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  try {
    const authToken = await waitForAuthToken();
    const config = getConfig();
    if (!config.publishableKey) {
      throw new Error('No publishable key configured');
    }
    const defaultOptions = {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'X-Publishable-Key': config.publishableKey,
        'X-App-Slug': config.appSlug || 'default',
        'Content-Type': 'application/json',
        ...options.headers
      }
    };
    const mergedOptions = {
      ...options,
      ...defaultOptions,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {})
      }
    };
    const response = await fetch(url, mergedOptions);

    // Handle token expiration
    if (response.status === 401) {
      // Token might be expired
      await logout();
      throw new Error('Authentication expired. Please login again.');
    }
    return response;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

/**
 * Make a GET request with authentication
 * @param {string} url - The URL to make the request to
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
const get = async function (url) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const response = await fetchWithAuth(url, {
    method: 'GET',
    ...options
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Make a POST request with authentication
 * @param {string} url - The URL to make the request to
 * @param {any} [data] - Data to send in the request body
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
const post = async function (url, data) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const response = await fetchWithAuth(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
    ...options
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Make a PUT request with authentication
 * @param {string} url - The URL to make the request to
 * @param {any} [data] - Data to send in the request body
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
const put = async function (url, data) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const response = await fetchWithAuth(url, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
    ...options
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

export { callWithAuth, fetchWithAuth, get, post, put };
//# sourceMappingURL=api.js.map
